import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Boxes, CheckCircle2, Save, Printer } from "lucide-react";
import { toast } from "sonner";

export default function AssignStock() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [stockByProduct, setStockByProduct] = useState({}); // pid -> in_stock cartons (unassigned OR assigned to this order)
  const [assignment, setAssignment] = useState({});         // cartonId -> productId  (local picked)
  const [serverAssigned, setServerAssigned] = useState({}); // cartonId -> true (already saved to this order)
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [orders, custs, prods, assigned] = await Promise.all([
      api.get("/orders"),
      api.get("/customers"),
      api.get("/products"),
      api.get(`/orders/${id}/assigned`),
    ]);
    const o = orders.data.find((x) => x.id === id);
    setOrder(o);
    setCustomer(custs.data.find((c) => c.id === o?.customer_id));
    setProducts(prods.data);
    // Build stock per product: in_stock cartons that are either unassigned or assigned to this order
    const map = {};
    const pickInit = {};
    const serverInit = {};
    for (const p of prods.data) {
      const r = await api.get(`/products/${p.id}/boxes`);
      const eligible = r.data.filter((b) =>
        b.status === "in_stock" &&
        (!b.assigned_order_id || b.assigned_order_id === id),
      );
      map[p.id] = eligible;
      for (const b of eligible) {
        if (b.assigned_order_id === id) {
          pickInit[b.id] = p.id;
          serverInit[b.id] = true;
        }
      }
    }
    setStockByProduct(map);
    setAssignment(pickInit);
    setServerAssigned(serverInit);
    // sanity check assigned summary too
    return assigned.data;
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e?.response?.data?.detail || "Failed to load"))); /* eslint-disable-next-line */ }, [id]);

  const productById = useMemo(() => {
    const m = {}; products.forEach((p) => { m[p.id] = p; }); return m;
  }, [products]);

  const requiredByPid = useMemo(() => {
    const m = {};
    (order?.items || []).forEach((it) => {
      if (it.product_id) m[it.product_id] = (m[it.product_id] || 0) + (parseInt(it.quantity_rolls) || 0);
    });
    return m;
  }, [order]);

  const pickedByPid = useMemo(() => {
    const m = {};
    Object.values(assignment).forEach((pid) => { m[pid] = (m[pid] || 0) + 1; });
    return m;
  }, [assignment]);

  const pickedCartons = useMemo(() => Object.keys(assignment), [assignment]);
  const totalNet = useMemo(() => {
    let n = 0;
    Object.entries(stockByProduct).forEach(([pid, list]) => {
      list.forEach((b) => { if (assignment[b.id]) n += b.net_weight_kg; });
    });
    return n;
  }, [stockByProduct, assignment]);
  const totalGross = useMemo(() => {
    let g = 0;
    Object.entries(stockByProduct).forEach(([pid, list]) => {
      list.forEach((b) => { if (assignment[b.id]) g += b.gross_weight_kg; });
    });
    return g;
  }, [stockByProduct, assignment]);

  const toggle = (carton, pid) => {
    setAssignment((s) => {
      const n = { ...s };
      if (n[carton.id]) {
        // un-pick is always allowed
        delete n[carton.id];
        return n;
      }
      // Cap: do not allow picking more cartons than the order needs for this product.
      const required = requiredByPid[pid] || 0;
      const alreadyPicked = Object.values(n).filter((p) => p === pid).length;
      if (required > 0 && alreadyPicked >= required) {
        toast.warning(`Cannot assign more — ${required} carton${required === 1 ? "" : "s"} already picked for this SKU. Un-pick one first or close the order with fewer cartons.`);
        return s;
      }
      n[carton.id] = pid;
      return n;
    });
  };

  // Select / un-select ALL cartons currently available for a product (respects required cap).
  const toggleAll = (list, pid) => {
    setAssignment((s) => {
      const n = { ...s };
      const allPicked = list.every((b) => n[b.id]);
      if (allPicked) {
        // unpick all of THIS product
        list.forEach((b) => { delete n[b.id]; });
        return n;
      }
      const required = requiredByPid[pid] || 0;
      const alreadyPicked = Object.values(n).filter((p) => p === pid).length;
      const remaining = required > 0 ? Math.max(0, required - alreadyPicked) : list.length;
      let taken = 0;
      for (const b of list) {
        if (n[b.id]) continue;
        if (required > 0 && taken >= remaining) break;
        n[b.id] = pid;
        taken += 1;
      }
      if (required > 0 && (alreadyPicked + taken) >= required) {
        toast.info(`Reached required quantity (${required}). Pick paused at the cap.`);
      }
      return n;
    });
  };

  const save = async () => {
    if (!order) return;
    const currentIds = new Set(Object.keys(assignment));
    const serverIds = new Set(Object.keys(serverAssigned));
    const toAssign = [...currentIds].filter((cid) => !serverIds.has(cid));
    const toUnassign = [...serverIds].filter((cid) => !currentIds.has(cid));
    if (toAssign.length === 0 && toUnassign.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      if (toUnassign.length) {
        await api.post(`/orders/${id}/unassign`, { carton_ids: toUnassign });
      }
      if (toAssign.length) {
        await api.post(`/orders/${id}/assign`, { carton_ids: toAssign });
      }
      toast.success(`Saved: ${toAssign.length} assigned, ${toUnassign.length} released`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!order) return <div className="p-8 text-zinc-500 font-mono text-xs">LOADING…</div>;

  const lineItems = (order.items || []).filter((it) => it.product_id);
  const allMet = lineItems.length > 0 && lineItems.every((it) => (pickedByPid[it.product_id] || 0) >= it.quantity_rolls);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <Link to="/orders" className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-orange-700 inline-flex items-center gap-1 mb-1"><ArrowLeft size={12} /> Back to Orders</Link>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Assign Stock — {order.order_no}</h1>
          <div className="text-xs text-zinc-600 font-mono mt-1">
            {customer?.company || customer?.name || ""} · Delivery <span className="text-orange-700 font-semibold">{order.delivery_date}</span> · Status <span className="uppercase">{order.status.replace("_", " ")}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/orders/${id}/print?type=dispatch`} target="_blank" className="text-[11px] uppercase font-mono px-3 py-2 border border-zinc-300 bg-white hover:bg-zinc-100 inline-flex items-center gap-1" data-testid="open-challan"><Printer size={12} /> Delivery Challan</Link>
          <button data-testid="save-assignment" onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono px-4 py-2 flex items-center gap-2 disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save Assignment"}
          </button>
        </div>
      </div>

      {/* Order line summary */}
      <div className="bg-white border border-zinc-300" data-testid="assign-summary">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Category</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Required</th>
              <th className="text-right px-3 py-2 border-b border-zinc-300">Picked</th>
              <th className="text-left px-3 py-2 border-b border-zinc-300">Status</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {lineItems.map((it, i) => {
              const picked = pickedByPid[it.product_id] || 0;
              const ok = picked >= it.quantity_rolls;
              return (
                <tr key={i} className="border-b border-zinc-200">
                  <td className="px-3 py-2 font-semibold">{productById[it.product_id]?.sku || "—"}</td>
                  <td className="px-3 py-2 text-right">{it.quantity_rolls}</td>
                  <td className={`px-3 py-2 text-right ${ok ? "text-orange-700" : "text-zinc-700"}`}>{picked}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase border px-2 py-0.5 ${ok ? "bg-orange-50 border-orange-300 text-orange-700" : "bg-zinc-100 border-zinc-300 text-zinc-700"}`}>
                      {ok ? "✓ Fulfilled" : `${it.quantity_rolls - picked} more needed`}
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-zinc-50 font-semibold">
              <td className="px-3 py-2 uppercase text-[10px] tracking-wider text-zinc-700">Totals</td>
              <td className="px-3 py-2 text-right">{lineItems.reduce((s, it) => s + it.quantity_rolls, 0)} ctns</td>
              <td className="px-3 py-2 text-right text-orange-700">{Object.keys(assignment).length} ctns</td>
              <td className="px-3 py-2 text-[11px]">Net {totalNet.toFixed(2)} · Gross {totalGross.toFixed(2)} · Tare {(totalGross - totalNet).toFixed(2)} kg</td>
            </tr>
          </tbody>
        </table>
        {allMet && (
          <div className="border-t border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-800 flex items-center gap-2"><CheckCircle2 size={14} /> All line items have stock assigned. Save to lock and proceed to dispatch.</div>
        )}
      </div>

      {/* Per-category stock pickers */}
      <div className="space-y-6" data-testid="assign-pickers">
        {lineItems.map((it, idx) => {
          const pid = it.product_id;
          const list = (stockByProduct[pid] || []).slice().sort((a, b) => (a.production_date || "").localeCompare(b.production_date || ""));
          const picked = pickedByPid[pid] || 0;
          const need = it.quantity_rolls;
          return (
            <div key={idx} className="bg-white border border-zinc-300" data-testid={`picker-${idx}`}>
              <div className="px-4 py-2 border-b border-zinc-300 bg-zinc-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Boxes size={14} className="text-zinc-600" />
                  <div className="text-sm font-semibold">{productById[pid]?.sku || "—"}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Need {need} ctns</div>
                </div>
                <div className="text-xs font-mono">
                  Picked <span className="text-orange-700 font-semibold">{picked}</span> / {need} · Available {list.length}
                </div>
              </div>
              {list.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-zinc-500 font-sans">No in-stock cartons available for this category. Produce more or release stock from another order.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-zinc-50 text-zinc-500 uppercase text-[9px] font-mono tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-1.5 border-b border-zinc-200 w-10">
                          <input
                            data-testid={`select-all-${pid}`}
                            type="checkbox"
                            checked={list.length > 0 && list.every((b) => !!assignment[b.id])}
                            onChange={() => toggleAll(list, pid)}
                            disabled={picked >= need && !list.some((b) => assignment[b.id])}
                            className="accent-orange-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            title={picked >= need ? `Cap reached (${need} cartons assigned)` : "Select / un-select all visible cartons of this category"}
                          />
                        </th>
                        <th className="text-left px-3 py-1.5 border-b border-zinc-200">Box No</th>
                        <th className="text-left px-3 py-1.5 border-b border-zinc-200">Production Date</th>
                        <th className="text-left px-3 py-1.5 border-b border-zinc-200">Location</th>
                        <th className="text-right px-3 py-1.5 border-b border-zinc-200">Net (kg)</th>
                        <th className="text-right px-3 py-1.5 border-b border-zinc-200">Gross (kg)</th>
                        <th className="text-right px-3 py-1.5 border-b border-zinc-200">Tare (kg)</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {list.map((b) => {
                        const isPicked = !!assignment[b.id];
                        const atCap = !isPicked && picked >= need && need > 0;
                        return (
                          <tr
                            key={b.id}
                            data-testid={`carton-${b.id}`}
                            onClick={() => { if (!atCap) toggle(b, pid); }}
                            className={`border-b border-zinc-200 ${atCap ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${isPicked ? "bg-orange-50 hover:bg-orange-100" : atCap ? "bg-zinc-50" : "hover:bg-zinc-50"}`}
                            title={atCap ? `Required quantity (${need}) already met` : ""}
                          >
                            <td className="px-3 py-2"><input type="checkbox" readOnly checked={isPicked} disabled={atCap} className="accent-orange-600" /></td>
                            <td className="px-3 py-2 font-semibold">{b.box_no}</td>
                            <td className="px-3 py-2 text-xs">{b.production_date}</td>
                            <td className="px-3 py-2 text-xs">{b.location || "Kani"}</td>
                            <td className="px-3 py-2 text-right">{b.net_weight_kg.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-orange-700">{b.gross_weight_kg.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-zinc-600">{(b.gross_weight_kg - b.net_weight_kg).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
